import { ImgID } from "../../image_loader/img_id.js";
import { ColorScheme } from "../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../common_ui_elements/text_style_scheme.js";
import { UIImage } from "../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../common_ui_elements/ui_nine_slice_element.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../common_ui_elements/ui_text_element.js";
import { InviteService } from "../../friends/invite_service.js";
import { AbstractDialog } from "./abstract_dialog.js";
import { DialogManager } from "./dialog_manager.js";

export class InviteFriendDialog extends AbstractDialog
{
  private static readonly DESIGN_W = 909;
  private static readonly BG_Y = 33;
  private static readonly BG_H = 1290;
  private static readonly DESIGN_H = InviteFriendDialog.BG_Y + InviteFriendDialog.BG_H;
  private static readonly PIC_W = 807;
  private static readonly PIC_H = 555;
  private static readonly PIC_Y = InviteFriendDialog.BG_Y + 10;
  private static readonly TEXT_Y = InviteFriendDialog.PIC_Y + InviteFriendDialog.PIC_H + 20;
  private static readonly REWARD_Y = InviteFriendDialog.TEXT_Y + 170;
  private static readonly REWARD_H = 140;
  private static readonly DEFAULT_REWARD_COINS = 5000;

  private reward_text?: UITextElement;

  constructor()
  {
    const body_w = LayoutScaler.widthFromDesign( InviteFriendDialog.DESIGN_W );
    const body_h = LayoutScaler.heightFromDesign( InviteFriendDialog.DESIGN_H );

    const body_x = ( DialogManager.getWidth() - body_w ) / 2;
    const body_y = ( DialogManager.getHeight() - body_h ) / 2;

    super( DialogManager.ctx, body_x, body_y, body_w, body_h );

    this.createBackground();
    this.createPicture();
    this.createText();
    this.createReward();
    this.createCloseButton();
    this.createActionButton();

    void InviteService.refresh().then( summary =>
    {
      if ( summary )
        this.reward_text?.setText( `+${summary.reward_coins}` );
    } );
  }

  private createBackground(): void
  {
    const bg_x = LayoutScaler.xFromDesign( 0 );
    const bg_y = LayoutScaler.yFromDesign( InviteFriendDialog.BG_Y );
    const bg_w = LayoutScaler.widthFromDesign( InviteFriendDialog.DESIGN_W );
    const bg_h = LayoutScaler.heightFromDesign( InviteFriendDialog.BG_H );

    const bg = new UINineSliceElement( this.ctx, bg_x, bg_y, bg_w, bg_h, ImgID.UI_CapsuleBlue, "capsule_bg" );
    this.addChild( bg );
  }

  private createPicture(): void
  {
    const design_pic_x = ( InviteFriendDialog.DESIGN_W - InviteFriendDialog.PIC_W ) / 2;

    const pic_x = LayoutScaler.xFromDesign( design_pic_x );
    const pic_y = LayoutScaler.yFromDesign( InviteFriendDialog.PIC_Y );
    const pic_w = LayoutScaler.widthFromDesign( InviteFriendDialog.PIC_W );
    const pic_h = LayoutScaler.heightFromDesign( InviteFriendDialog.PIC_H );

    const pic = new UIImage( this.ctx, pic_x, pic_y, pic_w, pic_h, ImgID.UI_InviteFriendPic );
    this.addChild( pic );
  }

  private createText(): void
  {
    const full_w = LayoutScaler.widthFromDesign( InviteFriendDialog.DESIGN_W );
    const MAGIC_NUM_CORRECTION = 0.75;
    const text_w = full_w * MAGIC_NUM_CORRECTION;
    const text_x = ( full_w - text_w ) / 2;
    const text_y = LayoutScaler.yFromDesign( InviteFriendDialog.TEXT_Y );
    const text_h = LayoutScaler.heightFromDesign( 150 );

    const text = new UITextElement(
      this.ctx,
      text_x,
      text_y,
      text_w,
      text_h,
      {
        localization_key: "INVITE_FRIEND.TEXT",
        font_size: LayoutScaler.textSizeFromDesign( 60 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: true,
        outline_color: ColorScheme.invite_friend_text_outline,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
      }
    );
    this.addChild( text );
  }

  private createReward(): void
  {
    const design_amount_w = 420;
    const design_coins_size = 190;
    const design_gap = 10;
    const design_row_w = design_amount_w + design_gap + design_coins_size;
    const design_row_x = ( InviteFriendDialog.DESIGN_W - design_row_w ) / 2;
    const design_coins_x = design_row_x + design_amount_w + design_gap;
    const design_coins_y = InviteFriendDialog.REWARD_Y + ( InviteFriendDialog.REWARD_H - design_coins_size ) / 2;

    const amount_x = LayoutScaler.xFromDesign( design_row_x );
    const amount_y = LayoutScaler.yFromDesign( InviteFriendDialog.REWARD_Y );
    const amount_w = LayoutScaler.widthFromDesign( design_amount_w );
    const amount_h = LayoutScaler.heightFromDesign( InviteFriendDialog.REWARD_H );

    const amount = new UITextElement(
      this.ctx,
      amount_x,
      amount_y,
      amount_w,
      amount_h,
      {
        text: `+${InviteService.get()?.reward_coins ?? InviteFriendDialog.DEFAULT_REWARD_COINS}`,
        font_size: LayoutScaler.textSizeFromDesign( 120 ),
        font_weight: 900,
        text_style: TextStyleScheme.rubik,
        text_color: ColorScheme.friends_invite_reward,
        text_align: MyTextAlign.Right,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 14 ),
        outline_color: ColorScheme.friends_invite_reward_outline,
        scale_to_width: true,
        auto_wrap: false,
      }
    );
    this.addChild( amount );
    this.reward_text = amount;

    const coins_x = LayoutScaler.xFromDesign( design_coins_x );
    const coins_y = LayoutScaler.yFromDesign( design_coins_y );
    const coins_w = LayoutScaler.widthFromDesign( design_coins_size );
    const coins_h = LayoutScaler.heightFromDesign( design_coins_size );

    const coins = new UIImage( this.ctx, coins_x, coins_y, coins_w, coins_h, ImgID.UI_ShopBlockImg );
    this.addChild( coins );
  }

  private createCloseButton(): void
  {
    const design_btn_size = 114;
    const design_btn_x = InviteFriendDialog.DESIGN_W - 80;

    const btn_x = LayoutScaler.xFromDesign( design_btn_x );
    const btn_y = LayoutScaler.yFromDesign( 0 );
    const btn_w = LayoutScaler.widthFromDesign( design_btn_size );
    const btn_h = LayoutScaler.heightFromDesign( design_btn_size );

    const btn = new UIImage( this.ctx, btn_x, btn_y, btn_w, btn_h, ImgID.UI_SettingsDialogCloseBtn, undefined, true );
    btn.setOnClick( () => DialogManager.closeDialog() );
    this.addChild( btn );
  }

  private createActionButton(): void
  {
    const design_btn_w = 700;
    const design_btn_h = 280;
    const design_btn_y = InviteFriendDialog.DESIGN_H - design_btn_h - 57;
    const design_btn_x = ( InviteFriendDialog.DESIGN_W - design_btn_w ) / 2;

    const btn_x = LayoutScaler.xFromDesign( design_btn_x );
    const btn_y = LayoutScaler.yFromDesign( design_btn_y );
    const btn_w = LayoutScaler.widthFromDesign( design_btn_w );
    const btn_h = LayoutScaler.heightFromDesign( design_btn_h );

    const btn = new UIImage( this.ctx, btn_x, btn_y, btn_w, btn_h, ImgID.UI_GreenFrameBtn, undefined, true );

    const text = new UITextElement(
      this.ctx, 0, 0, btn_w, btn_h,
      {
        localization_key: "INVITE_FRIEND.BUTTON",
        font_size: LayoutScaler.textSizeFromDesign( 80 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        scale_to_width: true,
        auto_wrap: false,
        outline_color: ColorScheme.menu_button_outline,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
        ...TextStyleScheme.buttonShadow(),
      }
    );
    btn.addChild( text, true );

    btn.setOnClick( () => void InviteService.share() );
    this.addChild( btn );
  }

  public name(): string
  {
    return "InviteFriendDialog";
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    this.startRender();
    this.finishRender();
  }
}
